/**
 * P1.6 T28 — i18next setup for the Mini App.
 *
 * Detection chain (highest → lowest priority):
 *   1. User.language (server-persisted, set from the Language picker)
 *   2. Telegram WebApp.initDataUnsafe.user.language_code (browser/Telegram hint)
 *   3. 'en' fallback
 *
 * Namespaces:
 *   - common   — shared UI strings (buttons, errors, empty states)
 *   - home     — Home hub
 *   - me       — Me hub + sub-pages
 *   - settings — Settings hub + sub-pages
 *   - flows    — onboarding + moon-phase + cosmic flows
 *   - course   — free astrology course (Мой курс)
 *
 * Brand terms in latin script (e.g. "Star Persona", "Cosmic Self") are NEVER
 * translated — they live as literal keys in the EN bundles and the RU/UK
 * bundles mirror them verbatim.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from './locales/en/common.json';
import enHome from './locales/en/home.json';
import enMe from './locales/en/me.json';
import enSettings from './locales/en/settings.json';
import enFlows from './locales/en/flows.json';
import enCourse from './locales/en/course.json';

import ruCommon from './locales/ru/common.json';
import ruHome from './locales/ru/home.json';
import ruMe from './locales/ru/me.json';
import ruSettings from './locales/ru/settings.json';
import ruFlows from './locales/ru/flows.json';
import ruCourse from './locales/ru/course.json';

import ukCommon from './locales/uk/common.json';
import ukHome from './locales/uk/home.json';
import ukMe from './locales/uk/me.json';
import ukSettings from './locales/uk/settings.json';
import ukFlows from './locales/uk/flows.json';
import ukCourse from './locales/uk/course.json';

import { skuBlockForLocale, tipsFragmentForLocale } from './shared-content';

export const SUPPORTED_LOCALES = ['en', 'ru', 'uk'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const I18N_NAMESPACES = ['common', 'home', 'me', 'settings', 'flows', 'course'] as const;
export const DEFAULT_LOCALE: Locale = 'en';

export function normaliseLocale(input: string | undefined | null): Locale {
  if (!input) return DEFAULT_LOCALE;
  const lc = input.toLowerCase().split('-')[0] ?? '';
  return (SUPPORTED_LOCALES as readonly string[]).includes(lc) ? (lc as Locale) : DEFAULT_LOCALE;
}

/**
 * Resolve the initial locale from the detection chain. Pure function so tests
 * can exercise every branch without spinning up the i18next runtime.
 */
export function resolveInitialLocale(opts: {
  userLanguage?: string | null;
  telegramLanguageCode?: string | null;
}): Locale {
  if (opts.userLanguage) return normaliseLocale(opts.userLanguage);
  if (opts.telegramLanguageCode) return normaliseLocale(opts.telegramLanguageCode);
  return DEFAULT_LOCALE;
}

// Merge the shared single-source content (SKU display strings, feature tips)
// into the static JSON bundles. `sku` is added to `common`; each tool's `tips`
// is deep-merged into the existing `flows.<tool>` object (keeping its siblings).
function withSharedCommon(base: object, loc: Locale): object {
  return { ...base, sku: skuBlockForLocale(loc) };
}
function withSharedFlows(base: Record<string, unknown>, loc: Locale): object {
  const merged: Record<string, unknown> = { ...base };
  for (const [tool, frag] of Object.entries(tipsFragmentForLocale(loc))) {
    merged[tool] = { ...(base[tool] as object | undefined), ...frag };
  }
  return merged;
}

const resources = {
  en: { common: withSharedCommon(enCommon, 'en'), home: enHome, me: enMe, settings: enSettings, flows: withSharedFlows(enFlows, 'en'), course: enCourse },
  ru: { common: withSharedCommon(ruCommon, 'ru'), home: ruHome, me: ruMe, settings: ruSettings, flows: withSharedFlows(ruFlows, 'ru'), course: ruCourse },
  uk: { common: withSharedCommon(ukCommon, 'uk'), home: ukHome, me: ukMe, settings: ukSettings, flows: withSharedFlows(ukFlows, 'uk'), course: ukCourse },
};

let initialised = false;
export function initI18n(initialLocale: Locale = DEFAULT_LOCALE): typeof i18n {
  if (initialised) {
    if (i18n.language !== initialLocale) void i18n.changeLanguage(initialLocale);
    return i18n;
  }
  void i18n.use(initReactI18next).init({
    resources,
    lng: initialLocale,
    fallbackLng: DEFAULT_LOCALE,
    ns: [...I18N_NAMESPACES],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  initialised = true;
  return i18n;
}

export default i18n;
